import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Shield, UserPlus, Trash2, AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface UserRole {
  id: string;
  user_id: string;
  role: 'admin' | 'user';
  created_at: string;
  user_email?: string;
}

export const AdminRolesManager = () => {
  const { toast } = useToast();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<UserRole | null>(null);

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      
      // Buscar roles de admin (EXCETO o super-admin)
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('role', 'admin')
        .order('created_at', { ascending: false });

      if (rolesError) throw rolesError;

      // Para cada role, buscar o email do usuário e filtrar o super-admin
      const rolesWithEmails = await Promise.all(
        (rolesData || []).map(async (role) => {
          const { data: userData } = await supabase
            .from('users')
            .select('email')
            .eq('id', role.user_id)
            .single();
          
          return {
            ...role,
            user_email: userData?.email || 'Email não encontrado'
          };
        })
      );

      // 🔒 FILTRAR SUPER-ADMIN da lista exibida
      const filteredRoles = rolesWithEmails.filter(
        role => role.user_email !== 'wagner.jeveaux@gmail.com'
      );

      setRoles(filteredRoles);
    } catch (error: any) {
      console.error('Erro ao buscar roles:', error);
      toast({
        title: "Erro ao carregar administradores",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) {
      toast({
        title: "Email inválido",
        description: "Por favor, insira um email válido.",
        variant: "destructive",
      });
      return;
    }

    // 🔒 PROTEÇÃO: Prevenir adição manual do super-admin
    if (newAdminEmail.trim().toLowerCase() === 'wagner.jeveaux@gmail.com') {
      toast({
        title: "Operação desnecessária",
        description: "Este usuário já é o super-administrador permanente do sistema.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAdding(true);

      // Buscar user_id pelo email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('email', newAdminEmail.trim())
        .single();

      if (userError || !userData) {
        toast({
          title: "Usuário não encontrado",
          description: "Não foi possível encontrar um usuário com este email.",
          variant: "destructive",
        });
        return;
      }

      // Inserir role admin
      const { error: insertError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userData.id,
          role: 'admin'
        });

      if (insertError) {
        if (insertError.code === '23505') { // Unique violation
          toast({
            title: "Usuário já é admin",
            description: "Este usuário já possui permissões de administrador.",
            variant: "destructive",
          });
        } else {
          throw insertError;
        }
        return;
      }

      toast({
        title: "Admin adicionado",
        description: `${newAdminEmail} agora é administrador.`,
      });

      setNewAdminEmail('');
      fetchRoles();
    } catch (error: any) {
      console.error('Erro ao adicionar admin:', error);
      toast({
        title: "Erro ao adicionar admin",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    // 🔒 PROTEÇÃO: Nunca permitir deletar o super-admin
    if (roleToDelete.user_email === 'wagner.jeveaux@gmail.com') {
      toast({
        title: "Operação bloqueada",
        description: "O super-administrador não pode ser removido.",
        variant: "destructive",
      });
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      return;
    }

    try {
      // Verificar se não é o último admin
      if (roles.length === 1) {
        toast({
          title: "Operação não permitida",
          description: "Deve haver pelo menos um administrador no sistema.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', roleToDelete.id);

      if (error) throw error;

      toast({
        title: "Admin removido",
        description: `${roleToDelete.user_email} não é mais administrador.`,
      });

      fetchRoles();
    } catch (error: any) {
      console.error('Erro ao remover admin:', error);
      toast({
        title: "Erro ao remover admin",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  const confirmDelete = (role: UserRole) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle>Gestão de Administradores</CardTitle>
          </div>
          <CardDescription>
            Gerencie quem tem acesso ao painel administrativo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Adicionar novo admin */}
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Email do novo administrador"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddAdmin()}
              disabled={isAdding}
            />
            <Button 
              onClick={handleAddAdmin} 
              disabled={isAdding || !newAdminEmail.trim()}
            >
              {isAdding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adicionando...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Adicionar
                </>
              )}
            </Button>
          </div>

          {/* Alerta de segurança */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-300">
              <strong>Atenção:</strong> Administradores têm acesso total ao sistema, incluindo gerenciamento de usuários e configurações críticas.
            </div>
          </div>

          {/* Lista de admins */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">
              Administradores Atuais ({roles.length})
            </h4>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum administrador encontrado
              </div>
            ) : (
              <div className="space-y-2">
                {roles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Shield className="h-4 w-4 text-primary" />
                      <div>
                        <div className="font-medium">{role.user_email}</div>
                        <div className="text-xs text-muted-foreground">
                          Desde {new Date(role.created_at).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                      <Badge variant="secondary">Admin</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => confirmDelete(role)}
                      disabled={roles.length === 1}
                      title={roles.length === 1 ? "Deve haver pelo menos um admin" : "Remover admin"}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog de confirmação */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Administrador</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{roleToDelete?.user_email}</strong> da lista de administradores?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRole} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
